import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  DashboardCard,
  TableChip,
  getStageChipTone,
} from '../../components/PremiumDesign';
import { Button, SkeletonText } from '@/components';
import { MailIcon, PhoneIcon } from '@/components/Icons';
import { V2Table } from '@/common/components';
import { getCRMRecentLeads } from '../../api';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getDisplayPhone } from '../utils/phoneUtils';

const textSk = (width) => <SkeletonText width={width} height="h-4" />;
const chipSk = (
  <SkeletonText width="w-20" height="h-6" className="rounded-full" />
);
const contactSk = (
  <div className="flex flex-col gap-1.5">
    <SkeletonText width="w-28" height="h-4" />
    <SkeletonText width="w-36" height="h-3" />
  </div>
);

function present(value) {
  if (value == null) return '';
  return String(value).trim();
}

function copyContact(event, text) {
  event.preventDefault();
  event.stopPropagation();
  navigator.clipboard.writeText(text).then(
    () => toast.success('Copied'),
    () => toast.error('Could not copy'),
  );
}

function LeadContact({ lead }) {
  const phoneText = present(lead.phone) || present(lead.phone_normalized);
  const phoneLabel = phoneText ? getDisplayPhone(lead) : '';
  const showPhone = phoneLabel && phoneLabel !== '-';
  const email = present(lead.email);

  if (!showPhone && !email) {
    return <span className="text-text-muted">Not provided</span>;
  }

  return (
    <div className="min-w-0 flex flex-col gap-0.5">
      {showPhone ? (
        <button
          type="button"
          title="Copy phone"
          onClick={(event) => copyContact(event, phoneLabel)}
          className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-left text-text-primary hover:underline"
        >
          <PhoneIcon className="w-3.5 h-3.5 shrink-0 text-text-muted" />
          <span className="truncate">{phoneLabel}</span>
        </button>
      ) : null}
      {email ? (
        <span className="relative min-w-0 max-w-full group/email">
          <button
            type="button"
            title={email}
            onClick={(event) => copyContact(event, email)}
            className={`inline-flex min-w-0 max-w-full items-center gap-1.5 text-left hover:underline focus:outline-none ${
              showPhone
                ? 'text-xs text-text-muted'
                : 'text-sm text-text-primary'
            }`}
          >
            <MailIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{email}</span>
          </button>
          <span
            role="tooltip"
            className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden max-w-[240px] break-all rounded-md border border-[#E2E8F0] bg-white px-2 py-1 text-xs text-text-primary shadow-sm group-hover/email:block group-focus-within/email:block"
          >
            {email}
          </span>
        </span>
      ) : null}
    </div>
  );
}

export function RecentOrdersTable() {
  const navigate = useNavigate();

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['crm-recent-leads', companyId],
    queryFn: () => getCRMRecentLeads(companyId, {}),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;
  const leads = data?.results || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    if (isError) {
      const apiErrorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load recent leads from the server.';
      toast.error(apiErrorMessage);
    }
  }, [isError, error]);

  const columns = [
    {
      id: 'sr',
      label: 'Sr No.',
      defaultWidth: 10,
      minWidth: 56,
      resizable: false,
      skeleton: textSk('w-8'),
      cell: (_row, index) => index + 1,
    },
    {
      id: 'customer',
      label: 'Customer',
      defaultWidth: 15,
      minWidth: 96,
      resizable: true,
      skeleton: textSk('w-28'),
      cell: (lead) => (
        <span className="truncate block font-medium">{lead.name || '-'}</span>
      ),
    },
    {
      id: 'contact',
      label: 'Contact',
      defaultWidth: 25,
      minWidth: 140,
      resizable: true,
      skeleton: contactSk,
      cell: (lead) => <LeadContact lead={lead} />,
    },
    {
      id: 'assigned',
      label: 'Assigned To',
      defaultWidth: 15,
      minWidth: 96,
      resizable: true,
      skeleton: textSk('w-24'),
      cell: (lead) => lead.assigned_to_name || 'Unassigned',
    },
    {
      id: 'created',
      label: 'Created Date',
      defaultWidth: 15,
      minWidth: 108,
      resizable: true,
      skeleton: textSk('w-24'),
      cell: (lead) => formatDate(lead.created_at),
    },
    {
      id: 'stage',
      label: 'Stage',
      defaultWidth: 20,
      minWidth: 96,
      resizable: true,
      skeleton: chipSk,
      cell: (lead) => (
        <TableChip tone={getStageChipTone(lead.current_stage?.name)}>
          {lead.current_stage?.name || 'N/A'}
        </TableChip>
      ),
    },
  ];

  return (
    <DashboardCard className="border border-border-default flex flex-col min-h-0 relative">
      <div className="flex items-center justify-between gap-3 mb-[20px]">
        <h3 className="text-[18px] leading-7 font-semibold text-[#1F2241]">
          Recent Leads
        </h3>
        <Button
          variant="primary"
          className="shrink-0"
          disabled={showSkeleton || !data}
          onClick={() => navigate('/crm-contacts')}
        >
          View all
          <span aria-hidden>→</span>
        </Button>
      </div>
      <V2Table
        bare
        compact
        columns={columns}
        data={leads}
        rowKey="id"
        fitProportional
        storageKey="crm_recent_leads_column_widths_v2"
        loading={showSkeleton || !data}
        loadingRowCount={5}
        empty="No leads found matching the query"
        onRowClick={(lead) =>
          navigate('/crm-pipeline', { state: { highlightLeadId: lead.id } })
        }
      />
    </DashboardCard>
  );
}
